import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useCreateFee, useUpdateFee } from '../../lib/resources/mutations';
import { parseError } from '../../lib/api/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import type { FeeConfigResponse } from '../../lib/resources/types';

// Mirrors CreateFeeConfigRequest / UpdateFeeConfigRequest in FeeConfigDtos.java.
// Backend rule: FIXED requires amountCents; PERCENT requires rateBasisPoints.
// Backend cap @Max(100_000) on rateBasisPoints → 1000% — surfaced as percentString (max 1000).
const feeSchema = z.object({
  type: z.enum(['FIXED', 'PERCENT']),
  name: z.string().min(1, 'Required').max(200, 'Max 200 chars'),
  description: z.string().max(1000, 'Max 1000 chars').optional(),
  amountString: z.string().optional(),
  percentString: z.string().optional(),
}).superRefine((v, ctx) => {
  if (v.type === 'FIXED') {
    if (!v.amountString || !/^\d+(\.\d{1,2})?$/.test(v.amountString)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['amountString'], message: 'e.g. 50 or 50.00' });
    }
  } else {
    if (!v.percentString || !/^\d+(\.\d{1,2})?$/.test(v.percentString)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['percentString'], message: 'e.g. 1.5' });
      return;
    }
    const pct = Number(v.percentString);
    if (pct < 0 || pct > 1000) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['percentString'], message: '0–1000%' });
    }
  }
});
type FeeFormValues = z.infer<typeof feeSchema>;

interface FeeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fee?: FeeConfigResponse | null;
}

export function FeeFormDialog({ open, onOpenChange, fee }: FeeFormDialogProps) {
  const isEdit = !!fee;
  const createFee = useCreateFee();
  const updateFee = useUpdateFee();
  const [serverError, setServerError] = useState<string | null>(null);

  const defaults: FeeFormValues = {
    type: fee?.type ?? 'FIXED',
    name: fee?.name ?? '',
    description: fee?.description ?? '',
    amountString: fee?.type === 'FIXED' && fee.amountCents != null
      ? (fee.amountCents / 100).toFixed(2) : '',
    percentString: fee?.type === 'PERCENT' && fee.rateBasisPoints != null
      ? (fee.rateBasisPoints / 100).toString() : '',
  };

  const form = useForm<FeeFormValues>({
    resolver: zodResolver(feeSchema),
    defaultValues: defaults,
  });

  const watchedType = form.watch('type');

  useEffect(() => {
    form.reset(defaults);
    setServerError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fee?.id, open]);

  async function onSubmit(values: FeeFormValues) {
    setServerError(null);
    try {
      const amountCents = values.type === 'FIXED' && values.amountString
        ? Math.round(Number(values.amountString) * 100) : undefined;
      const rateBasisPoints = values.type === 'PERCENT' && values.percentString
        ? Math.round(Number(values.percentString) * 100) : undefined;

      if (isEdit && fee) {
        await updateFee.mutateAsync({
          id: fee.id,
          body: {
            type: values.type,
            name: values.name,
            description: values.description || undefined,
            amountCents,
            rateBasisPoints,
          },
        });
      } else {
        await createFee.mutateAsync({
          type: values.type,
          name: values.name,
          description: values.description || undefined,
          amountCents,
          rateBasisPoints,
        });
      }
      onOpenChange(false);
    } catch (e) {
      const err = await parseError(e);
      setServerError(err.message || 'Save failed');
    }
  }

  const pending = isEdit ? updateFee.isPending : createFee.isPending;
  const errors = form.formState.errors;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${fee?.name}` : 'New fee'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the fee details. Changes apply to master invoices that haven\'t been finalized yet.'
              : 'Fees apply on top of every master invoice. FIXED adds a flat amount; PERCENT applies a rate to the subtotal.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Type" error={errors.type?.message}>
              <select
                className="w-full h-9 px-3 rounded-[--radius-input] border border-border bg-background text-[13px]"
                {...form.register('type')}
              >
                <option value="FIXED">Fixed</option>
                <option value="PERCENT">Percent</option>
              </select>
            </FormField>
            <div className="col-span-2">
              <FormField label="Name" error={errors.name?.message}>
                <Input {...form.register('name')} />
              </FormField>
            </div>
          </div>

          {watchedType === 'FIXED' ? (
            <FormField label="Amount" error={errors.amountString?.message}>
              <Input
                inputMode="decimal"
                placeholder="50.00"
                className="font-mono"
                {...form.register('amountString')}
              />
            </FormField>
          ) : (
            <FormField label="Rate (%)" error={errors.percentString?.message}>
              <Input
                inputMode="decimal"
                placeholder="1.5"
                className="font-mono"
                {...form.register('percentString')}
              />
            </FormField>
          )}

          <FormField label="Description (optional)" error={errors.description?.message}>
            <Input {...form.register('description')} />
          </FormField>

          {serverError && (
            <div className="text-[12px] text-error" role="alert">{serverError}</div>
          )}

          <DialogFooter>
            <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="size-3.5 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create fee'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
      {error && <div className="text-[11px] text-error mt-1">{error}</div>}
    </div>
  );
}
